import { useState, useCallback, useEffect, useRef } from 'react';
import { AppContext } from './AppContext';
import { api } from '../services/api';
import {
  getCheckoutStateByProductInstanceId,
  createCheckoutState,
  updateCheckoutState,
} from '../api/checkoutState.api';
import { parseCheckoutJsonString, serializeCheckoutStateForApi } from '../helpers/checkoutJson';
import { getProductInstanceId, getCookieValue } from '../helpers/cookieHelper';
import { CURRENCIES } from '../utils/constants';
import { initializeFullstory } from '../trackers/fullstoryInit';
import { initializePendo } from '../trackers/pendoInit';
import { resolveCurrencyFromWhoisAddress } from '../helpers/currencyHelper';

const readProductId = () => {
  return getProductInstanceId();
};

const mapFallbackCurrencies = () =>
  CURRENCIES.map((c) => ({ code: c.value, name: c.label, symbol: null }));

const initialSnackbar = { open: false, message: '', severity: 'success' };

const initialState = {
  productType: 'standard',
  product: {
    name: '',
    description: '',
    price: 0,
    currency: '',
    imageUrl: '',
    digitalFileUrl: '',
    internalFilename: '',
    digitalFileContentType: '',
    digitalFileName: '',
    digitalFileContentLength: 0,
    /** JPEG data URL thumbnail for image digital files; persisted in checkout_json for builder preview after reload */
    digitalFilePreviewDataUrl: '',
  },
  businessInfo: {
    companyName: '',
    email: '',
    address: '',
  },
  productId: null,
  isProductCreated: false,
  checkoutPageId: null,
  appInstanceId: null,
};

export const AppProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [state, setState] = useState(initialState);
  const [isSaving, setIsSaving] = useState(false);
  const [storeId, setStoreId] = useState(null);
  const [pid] = useState(() => readProductId());
  const [snackbar, setSnackbar] = useState(initialSnackbar);
  const [checkoutStateLoaded, setCheckoutStateLoaded] = useState(false);
  const [currencyOptions, setCurrencyOptions] = useState(mapFallbackCurrencies);
  const [currenciesLoading, setCurrenciesLoading] = useState(false);
  const [sslConnected, setSslConnected] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [checkoutStateLoading, setCheckoutStateLoading] = useState(false);
  const [businessInfoLoading, setBusinessInfoLoading] = useState(false);
  const [paymentProvidersLoading, setPaymentProvidersLoading] = useState(false);
  const [mfeAccountLoading, setMfeAccountLoading] = useState(false);
  const businessInfoRef = useRef(initialState.businessInfo);
  const productCurrencyRef = useRef(initialState.product.currency);

  useEffect(() => {
    initializeFullstory();
    initializePendo(token);
  }, [token]);

  useEffect(() => {
    businessInfoRef.current = state.businessInfo;
  }, [state.businessInfo]);

  useEffect(() => {
    productCurrencyRef.current = state.product.currency;
  }, [state.product.currency]);

  // Get token on mount and refresh every 5 minutes
  useEffect(() => {
    const getToken = async (isInitial = false) => {
      if (isInitial) {
        setTokenLoading(true);
      }
      try {
        const response = await api.getToken();
        if (response.success) {
          const tokenValue = response?.data?.response?.data?.a_token;
          setToken(tokenValue);
          if (tokenValue) {
            localStorage.setItem('authToken', tokenValue);
          }
        }
      } finally {
        if (isInitial) {
          setTokenLoading(false);
        }
      }
    };
    getToken(true);
    const intervalId = setInterval(() => {
      getToken(false);
    }, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!token) return;

    const checkoutDomain = getCookieValue('checkout_domain');
    let intervalId = null;

    const findSslStatusForDomain = (data, domain) => {
      if (!domain || !Array.isArray(data?.subscriptions)) return null;
      const normalizedDomain = String(domain).trim().toLowerCase();
      for (const subscription of data.subscriptions) {
        const domains = subscription?.domains || [];
        for (const entry of domains) {
          if (
            entry?.domain &&
            String(entry.domain).trim().toLowerCase() === normalizedDomain
          ) {
            return entry.sslStatus;
          }
        }
      }
      return null;
    };

    const checkSsl = async (isInitial = false) => {
      if (isInitial) {
        setMfeAccountLoading(true);
      }
      try {
        const data = await api.getMfeAccount();
        const sslStatus = findSslStatusForDomain(data, checkoutDomain);
        const connected = sslStatus === 'connected';
        setSslConnected(connected);
        if (connected && intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      } catch (error) {
        console.error('Failed to fetch MFE account for SSL check:', error);
        setSslConnected(false);
      } finally {
        if (isInitial) {
          setMfeAccountLoading(false);
        }
      }
    };

    checkSsl(true);
    intervalId = setInterval(() => checkSsl(false), 10 * 60 * 1000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [token]);

  // Get store ID
  useEffect(() => {
    const getStore = async () => {
      if (!token) return;
      const response = await api.getStore();
      if (response) {
        if (response.store_id != null) {
          localStorage.setItem('storeId', response.store_id);
        } else {
          localStorage.removeItem('storeId');
        }
        setStoreId(response.store_id);
        if (response.app_instance_id != null) {
          localStorage.setItem('appInstanceId', response.app_instance_id);
          setState((prev) => ({
            ...prev,
            appInstanceId: response.app_instance_id,
          }));
        }
      }
    };
    getStore();
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (storeId == null) {
        setCurrencyOptions(mapFallbackCurrencies());
        return;
      }
      setCurrenciesLoading(true);
      const data = await api.getCurrencies(storeId);
      if (cancelled) return;
      setCurrenciesLoading(false);
      if (Array.isArray(data) && data.length > 0) {
        setCurrencyOptions(data);
      } else {
        setCurrencyOptions(mapFallbackCurrencies());
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [storeId]);

  useEffect(() => {
    if (!checkoutStateLoaded) return;

    const checkoutDomain = getCookieValue('checkout_domain');
    if (!checkoutDomain) return;

    const currentBiz = businessInfoRef.current;
    const allPresent =
      currentBiz.companyName?.trim() &&
      currentBiz.email?.trim() &&
      currentBiz.address?.trim();
    if (allPresent) return;

    const fetchBusinessInfo = async () => {
      setBusinessInfoLoading(true);
      try {
        const response = await api.getBusinessInfo(checkoutDomain);
        const contacts = response?.response?.data?.whoisContacts ?? [];
        const accountHolder = contacts.find(
          (c) => c.whoisContactType === 'ACCOUNTHOLDER'
        );
        if (!accountHolder?.contactInfo) return;

        const { companyName, email, firstName, lastName } = accountHolder.contactInfo;
        const addr = accountHolder.contactInfo.address || {};
        const { address1, address2, city, stateProv, postalCode, country } = addr;
        const addressLine = [address1, address2, city, stateProv, postalCode, country]
          .filter((p) => p && String(p).trim())
          .join(', ');
        const fullName = [firstName, lastName]
          .filter((p) => p && String(p).trim())
          .join(' ')
          .trim();
        const resolvedCompanyName = companyName?.trim()
          ? companyName.trim()
          : fullName;

        setState((prev) => {
          const biz = prev.businessInfo;
          const updates = {};
          if (!biz.companyName?.trim() && resolvedCompanyName) {
            updates.companyName = resolvedCompanyName;
          }
          if (!biz.email?.trim() && email?.trim()) {
            updates.email = email.trim();
          }
          if (!biz.address?.trim() && addressLine) {
            updates.address = addressLine;
          }
          if (Object.keys(updates).length === 0) return prev;
          return {
            ...prev,
            businessInfo: {
              ...biz,
              ...updates,
            },
          };
        });
      } catch (error) {
        console.error('Failed to fetch business info:', error);
      } finally {
        setBusinessInfoLoading(false);
      }
    };

    fetchBusinessInfo();
  }, [checkoutStateLoaded]);

  useEffect(() => {
    if (!checkoutStateLoaded) return;
    if (productCurrencyRef.current && String(productCurrencyRef.current).trim()) return;
    if (currenciesLoading) return;

    const options = currencyOptions;
    const applyDefaultCurrencyIfEmpty = () => {
      setState((prev) => {
        const existing = prev.product.currency;
        if (existing && String(existing).trim()) return prev;
        const next = options.some((c) => c.code === 'USD')
          ? 'USD'
          : options[0]?.code;
        if (!next) return prev;
        return {
          ...prev,
          product: { ...prev.product, currency: next },
        };
      });
    };

    const checkoutDomain = getCookieValue('checkout_domain');
    if (!checkoutDomain) {
      applyDefaultCurrencyIfEmpty();
      return;
    }

    const fetchCurrencyFromCountry = async () => {
      try {
        const response = await api.getBusinessInfo(checkoutDomain);
        const contacts = response?.response?.data?.whoisContacts ?? [];
        const accountHolder = contacts.find(
          (c) => c.whoisContactType === 'ACCOUNTHOLDER'
        );
        const addr = accountHolder?.contactInfo?.address;
        const resolved = resolveCurrencyFromWhoisAddress(addr);
        if (!resolved) return;

        const isAvailable = options.some((c) => c.code === resolved);
        if (!isAvailable) return;

        setState((prev) => {
          const existing = prev.product.currency;
          if (existing && String(existing).trim()) return prev;
          return {
            ...prev,
            product: { ...prev.product, currency: resolved },
          };
        });
      } catch (error) {
        console.error('Failed to fetch currency from country:', error);
      } finally {
        applyDefaultCurrencyIfEmpty();
      }
    };

    fetchCurrencyFromCountry();
  }, [checkoutStateLoaded, currenciesLoading, currencyOptions]);

  // Load or create checkout state after token is available
  useEffect(() => {
    if (!token || !pid) return;
    const loadCheckoutState = async () => {
      setCheckoutStateLoading(true);
      try {
        const data = await getCheckoutStateByProductInstanceId(pid);
        if (data?.product_instance_id && data?.checkout_json != null) {
          const merged = parseCheckoutJsonString(data.checkout_json, initialState);
          setState(merged);
          if (merged.appInstanceId != null) {
            localStorage.setItem('appInstanceId', merged.appInstanceId);
          }
          setCheckoutStateLoaded(true);
          return;
        }
      } catch {
        // Fall through to create
      }
      await createCheckoutState({
        product_instance_id: pid,
        checkout_json: serializeCheckoutStateForApi(initialState),
      });
      setCheckoutStateLoaded(true);
    };
    loadCheckoutState().finally(() => {
      setCheckoutStateLoading(false);
    });
  }, [token, pid]);

  useEffect(() => {
    if (!token || !storeId) return;
    const loadPaymentProviders = async () => {
      setPaymentProvidersLoading(true);
      try {
        const paymentState = await api.getPaymentProviders(storeId);
        if (paymentState) {
          setState((prev) => ({
            ...prev,
            paymentProviders: paymentState,
          }));
        }
      } finally {
        setPaymentProvidersLoading(false);
      }
    };
    loadPaymentProviders();
  }, [token, storeId]);

  // Debounced auto-save to onboarding checkout-state (checkout_json only)
  useEffect(() => {
    if (!checkoutStateLoaded || !pid) return;
    const timer = setTimeout(async () => {
      setIsSaving(true);
      try {
        await updateCheckoutState(pid, {
          checkout_json: serializeCheckoutStateForApi(state),
        });
      } catch (error) {
        console.error('Checkout state save failed:', error);
      } finally {
        setIsSaving(false);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [state, checkoutStateLoaded, pid]);

  // Update product type
  const updateProductType = useCallback((productType) => {
    setState((prev) => {
      if (productType === prev.productType) {
        return prev;
      }
      return {
        ...prev,
        productType,
      };
    });
  }, []);

  // Update product fields
  const updateProduct = useCallback((updates) => {
    setState((prev) => ({
      ...prev,
      product: {
        ...prev.product,
        ...updates,
      },
    }));
  }, []);

  // Update business info
  const updateBusinessInfo = useCallback((updates) => {
    setState((prev) => ({
      ...prev,
      businessInfo: {
        ...prev.businessInfo,
        ...updates,
      },
    }));
  }, []);

  const updatePaymentProviders = useCallback((paymentProviders) => {
    setState((prev) => ({
      ...prev,
      paymentProviders,
    }));
  }, []);

  const updateProductCreated = useCallback((productId) => {
    setState((prev) => ({
      ...prev,
      isProductCreated: true,
      productId,
    }));
  }, []);

  const updateCheckoutPageId = useCallback((id) => {
    setState((prev) => ({ ...prev, checkoutPageId: id }));
  }, []);

  const setAppInstanceId = useCallback((appInstanceId) => {
    setState((prev) => ({ ...prev, appInstanceId }));
  }, []);

  // Get complete state for saving/publishing
  const getState = useCallback(() => {
    return state;
  }, [state]);

  // Reset state
  const resetState = useCallback(() => {
    setState(initialState);
  }, []);

  // Show snackbar
  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // Close snackbar
  const closeSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const value = {
    state,
    isSaving,
    setIsSaving,
    updateProductType,
    updateProduct,
    updateBusinessInfo,
    updatePaymentProviders,
    updateProductCreated,
    getState,
    resetState,
    token,
    setToken,
    storeId,
    setStoreId,
    currencyOptions,
    currenciesLoading,
    appInstanceId: state.appInstanceId,
    setAppInstanceId,
    pid,
    updateCheckoutPageId,
    snackbar,
    showSnackbar,
    closeSnackbar,
    sslConnected,
    tokenLoading,
    checkoutStateLoading,
    businessInfoLoading,
    paymentProvidersLoading,
    mfeAccountLoading,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
